import { useEffect, useMemo } from "react";
import { Form, Row, Col, Switch, message, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  updateEnterprisePoc,
  getEnterprisePocs,
  getDesignations,
  checkEnterprisePocEmail,
} from "../../../redux/slices/enterpriseSlice";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomInput from "../../../components/CustomInput/Input";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import FormFooter from "../../../components/CustomForms/FormFooter";
import {
  BTN_TXT,
  ERROR_MSGS,
  FORM_MODAL_WIDTHS,
  YESORNO,
} from "../../../constants/constants";
import {
  nameRules,
  emailRules,
  phoneNumberRules,
  designationRules,
  address1Rules,
  address2Rules,
  cityRules,
  stateRules,
  pincodeRules,
  getRemarksRules,
  getApobRules,
  REMARKS_MAX_LENGTH,
  capitalizeWords,
} from "../../../helpers/helpers";
import CancelButton from "../../../components/CustomButton/CancelButton";
import { CloseOutlined } from "@ant-design/icons";

const { Title } = Typography;

const OTHERS_DESIGNATION_VALUE = "OTHERS";

const isOthersName = (name) =>
  String(name || "")
    .trim()
    .toLowerCase() === "others";

const isOthersDesignation = (value, designations = []) => {
  if (!value) return false;
  if (value === OTHERS_DESIGNATION_VALUE) return true;
  const selected = designations.find((d) => d.designationId === value);
  return isOthersName(selected?.designationName);
};

const resolveDesignationName = (values, designations = []) => {
  if (isOthersDesignation(values.designationId, designations)) {
    return values.customDesignation?.trim();
  }
  return designations
    .find((d) => d.designationId === values.designationId)
    ?.designationName?.trim();
};

const DEPARTMENT_OPTIONS = [
  { label: "Finance", value: "Finance" },
  { label: "Accounts Payable", value: "Accounts Payable" },
  { label: "Procurement", value: "Procurement" },
  { label: "Legal & Compliance", value: "Legal & Compliance" },
  { label: "Operations", value: "Operations" },
  { label: "Supply Chain & Logistics", value: "Supply Chain & Logistics" },
  { label: "Human Resources", value: "Human Resources" },
  { label: "Executive / Management", value: "Executive / Management" },
  { label: "Sales & Marketing", value: "Sales & Marketing" },
];

const EditPocModal = ({ visible, onCancel, pocData, enterpriseId, readOnly = false }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { pocActionLoading, designationsData } = useSelector(
    (state) => state.enterprise,
  );
  const selectedDesignationId = Form.useWatch("designationId", form);
  const watchedHasApob = Form.useWatch("hasApob", form);
  const existingHasApob = Boolean(
    pocData?.apob || pocData?.address?.addressLine1,
  );
  const hasApob = existingHasApob || watchedHasApob === true;
  const showCustomDesignation = isOthersDesignation(
    selectedDesignationId,
    designationsData,
  );

  const designationOptions = useMemo(() => {
    const options =
      designationsData?.map((desig) => ({
        value: desig.designationId,
        label: capitalizeWords(desig.designationName),
      })) || [];

    if (!designationsData?.some((d) => isOthersName(d.designationName))) {
      options.push({ value: OTHERS_DESIGNATION_VALUE, label: "Others" });
    }

    return options;
  }, [designationsData]);

  useEffect(() => {
    dispatch(getDesignations());
  }, [dispatch]);

  const pocEmailRules = useMemo(
    () => [
      ...emailRules("POC"),
      {
        validateTrigger: "onBlur",
        validator: async (_, value) => {
          if (readOnly) return Promise.resolve();
          const trimmed = typeof value === "string" ? value.trim() : value;
          if (!trimmed) return Promise.resolve();
          if (
            !/^[a-zA-Z0-9]+(?:\.[a-zA-Z0-9]+)*@[a-zA-Z0-9]+(?:\.[a-zA-Z0-9]+)+$/.test(
              trimmed,
            )
          ) {
            return Promise.resolve();
          }

          const res = await dispatch(
            checkEnterprisePocEmail({
              entId: enterpriseId,
              email: trimmed,
              excludePocId: pocData?.pocId,
            }),
          );
          if (
            res?.meta?.requestStatus === "fulfilled" &&
            (res?.payload?.exists === true ||
              res?.payload?.response?.exists === true)
          ) {
            return Promise.reject(new Error("Email already exists"));
          }
          return Promise.resolve();
        },
      },
    ],
    [dispatch, enterpriseId, pocData?.pocId, readOnly],
  );

  useEffect(() => {
    if (pocData && visible) {
      const savedName = pocData.designation?.trim();
      const fields = {
        name: pocData.pocName || pocData.name,
        email: pocData.pocEmail || pocData.email,
        mobile: pocData.pocMobile || pocData.mobile,
        department: pocData.department,
        hasApob: Boolean(pocData.apob || pocData.address?.addressLine1),
        apob: pocData.apob,
        remarks: pocData.remarks,
        isActive: pocData.isActive !== false,
        address: pocData.address
          ? {
              addressLine1: pocData.address.addressLine1,
              addressLine2: pocData.address.addressLine2,
              city: pocData.address.city,
              state: pocData.address.state,
              pinCode: pocData.address.zipCode || pocData.address.pinCode,
            }
          : undefined,
      };

      if (designationsData?.length) {
        const matchedDesignation = designationsData.find(
          (d) =>
            d.designationName?.trim().toLowerCase() ===
            savedName?.toLowerCase(),
        );
        const othersFromDb = designationsData.find((d) =>
          isOthersName(d.designationName),
        );
        const useCustomDesignation =
          Boolean(savedName) &&
          (!matchedDesignation ||
            isOthersName(matchedDesignation.designationName));

        fields.designationId = useCustomDesignation
          ? othersFromDb?.designationId || OTHERS_DESIGNATION_VALUE
          : matchedDesignation?.designationId;
        fields.customDesignation = useCustomDesignation ? savedName : undefined;
      }

      form.setFieldsValue(fields);
    }
  }, [pocData, visible, form, designationsData]);

  const handleFinish = async (values) => {
    try {
      const shouldSaveApob = existingHasApob || values.hasApob;
      const payload = {
        name: values.name.trim(),
        email: values.email.trim(),
        mobile: values.mobile.trim(),
        department: values.department,
        apob: shouldSaveApob ? values.apob.trim() : null,
        designation: resolveDesignationName(values, designationsData),
        remarks: values.remarks ? values.remarks.trim() : null,
        isActive: values.isActive,
      };

      if (shouldSaveApob) {
        payload.address = {
          addressLine1: values.address.addressLine1.trim(),
          addressLine2: values.address.addressLine2?.trim() || "",
          city: values.address.city.trim(),
          state: values.address.state.trim(),
          pinCode: values.address.pinCode.trim(),
        };
      } else {
        payload.addressId = null;
      }

      const res = await dispatch(
        updateEnterprisePoc({
          pocId: pocData.pocId,
          data: payload,
        }),
      );

      if (
        res?.meta?.requestStatus === "fulfilled" ||
        res?.payload?.statusCode === 200
      ) {
        const mailSent = res?.payload?.response?.mailSent;
        const approvalMailTriggered =
          res?.payload?.response?.approvalMailTriggered;
        if (mailSent) {
          message.success(
            "Point of Contact updated. Approval email sent to the POC.",
          );
        } else if (approvalMailTriggered) {
          message.warning(
            "Point of Contact updated, but the approval email could not be sent. Use Resend Email.",
          );
        } else {
          message.success("Point of Contact updated successfully!");
        }
        onCancel();
        dispatch(getEnterprisePocs({ entId: enterpriseId }));
      } else {
        const errorMsg =
          res?.payload?.error ||
          res?.payload?.message ||
          (typeof res?.payload === "string" ? res.payload : null) ||
          "Failed to update POC";
        const isDuplicateEmail = /email already exists/i.test(String(errorMsg));
        if (isDuplicateEmail) {
          form.setFields([{ name: "email", errors: ["Email already exists"] }]);
        }
        message.error(errorMsg);
      }
    } catch (err) {
      message.error(err?.message || "An unexpected error occurred.");
    }
  };

  return (
    <CustomModal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>
            {readOnly ? "View" : "Edit"} Point of Contact -{" "}
            {pocData?.pocName || pocData?.name}
          </span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      closeIcon={<CloseOutlined />}
      width={FORM_MODAL_WIDTHS}
      centered
      destroyOnHidden
      content={
        <>
        <Form
          form={form}
          layout="vertical"
          onFinish={readOnly ? undefined : handleFinish}
          className="poc-modal-form"
          disabled={readOnly}
        >
          <Title level={5}>Contact Information</Title>

          <Row gutter={16}>
            <Col span={12}>
              <CustomInput
                name="name"
                label="Full Name"
                isRequired
                rules={nameRules("POC")}
              />
            </Col>

            <Col span={12}>
              <CustomInput
                name="email"
                type="email"
                label="Email Address"
                isRequired
                rules={pocEmailRules}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <CustomInput
                name="mobile"
                label="Mobile Number"
                isRequired
                allowNumbersOnly
                maxLength={10}
                rules={phoneNumberRules("POC")}
              />
            </Col>

            <Col span={12}>
              <CustomSelect
                name="designationId"
                label="Designation / Role"
                isRequired
                showSearch
                placeholder="Select Designation"
                options={designationOptions}
                rules={[
                  { required: true, message: ERROR_MSGS.DESIGNATION_ERR_MEG },
                ]}
                onChange={(value) => {
                  if (!isOthersDesignation(value, designationsData)) {
                    form.setFieldsValue({ customDesignation: undefined });
                  }
                }}
              />
            </Col>
          </Row>

          {showCustomDesignation && (
            <CustomInput
              name="customDesignation"
              label="Enter Designation"
              isRequired
              placeholder="Enter designation"
              rules={designationRules}
            />
          )}

          <Title level={5}>
            Department & Additional Place of Business (APOB)
          </Title>

          <Row gutter={16}>
            <Col span={existingHasApob ? 24 : 12}>
              <CustomSelect
                name="department"
                label="Department"
                isRequired
                showSearch
                options={DEPARTMENT_OPTIONS}
                rules={[
                  { required: true, message: "Please select department" },
                ]}
              />
            </Col>

            {!existingHasApob && (
              <Col span={12}>
                <CustomSelect
                  name="hasApob"
                  label="Has Additional Place of Business (APOB)?"
                  placeholder="Select Yes/No"
                  isRequired
                  options={YESORNO}
                  rules={[
                    {
                      required: true,
                      message: "Please select whether the POC has an APOB",
                    },
                  ]}
                  onChange={(value) => {
                    if (!value) {
                      form.setFieldsValue({
                        apob: undefined,
                        address: undefined,
                      });
                    }
                  }}
                />
              </Col>
            )}
          </Row>

          {hasApob && (
            <>
              <CustomInput
                name="apob"
                label="Additional Place of Business (APOB)"
                isRequired
                placeholder="e.g. Bengaluru Plant Unit 2"
                rules={getApobRules({ required: true, label: "APOB" })}
              />

              <Row gutter={16}>
                <Col span={12}>
                  <CustomInput
                    name={["address", "addressLine1"]}
                    label="Address Line 1"
                    isRequired
                    placeholder="Plot/Building/Street"
                    rules={address1Rules}
                  />
                </Col>
                <Col span={12}>
                  <CustomInput
                    name={["address", "addressLine2"]}
                    label="Address Line 2"
                    placeholder="Suite/Landmark/Area"
                    rules={address2Rules.filter((rule) => !rule.required)}
                  />
                </Col>
                <Col span={8}>
                  <CustomInput
                    name={["address", "city"]}
                    label="City"
                    isRequired
                    allowLettersOnly
                    placeholder="City"
                    rules={cityRules}
                  />
                </Col>
                <Col span={8}>
                  <CustomInput
                    name={["address", "state"]}
                    label="State"
                    isRequired
                    allowLettersOnly
                    placeholder="State"
                    rules={stateRules}
                  />
                </Col>
                <Col span={8}>
                  <CustomInput
                    name={["address", "pinCode"]}
                    label="PIN Code"
                    isRequired
                    allowNumbersOnly
                    placeholder="6-digit PIN"
                    maxLength={6}
                    rules={pincodeRules}
                  />
                </Col>
              </Row>
            </>
          )}

          <CustomInput
            name="remarks"
            label="Remarks / Notes"
            isTextArea
            rows={2}
            maxLength={REMARKS_MAX_LENGTH}
            rules={getRemarksRules({ required: false })}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              margin: "16px 0 20px",
              background: "#f3f4f6",
              padding: "8px 16px",
              borderRadius: 6,
            }}
          >
            <span style={{ fontWeight: 500, color: "#374151" }}>
              Active Status
            </span>
            <Form.Item name="isActive" valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
          </div>

          {!readOnly && (
            <FormFooter
              onCancelClick={onCancel}
              submitLoading={pocActionLoading}
              btnName={BTN_TXT.SAVE_CHNG}
            />
          )}
        </Form>
        {readOnly && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <CancelButton type="button" onClick={onCancel} />
          </div>
        )}
        </>
      }
    />
  );
};

export default EditPocModal;
